<template>
  <div
    class="text-input input"
    :class="{
      disabled,
      'has-icon': hasIcon,
      'rounded-edge': roundedEdge,
      'with-shadow': withShadow,
      'icon-right': (iconPosition === 'right'),
      borderless,
      borderbottom,
      textarea,
      'dynamic-label': dynamicLabel
    }"
  >
    <input
      v-bind:id="id"
      v-bind="$attrs"
      v-bind:type="type"
      v-bind:placeholder="placeholder"
      v-model="wrappedValue"
      v-bind:disabled="disabled"
      v-bind:hint="hint"
      v-on="inputListeners"
      ref="input"
      v-if="!textarea"
    />
    <textarea
      v-bind:id="id"
      v-bind="$attrs"
      v-bind:type="type"
      v-bind:placeholder="placeholder"
      v-model="wrappedValue"
      v-bind:disabled="disabled"
      v-bind:hint="hint"
      v-on="inputListeners"
      ref="input"
      v-if="!!textarea"
      rows="2"
    />

    <label :for="id" v-if="!!label" :class="{ mini: (dynamicLabel && !!(wrappedValue + '').length) }" >{{ label }} <span v-if="required && !!label">*</span></label>
    <span class="underline"></span>
    <slot v-if="hasIcon" name="icon" />

    <small class="red" v-if="!!validateMessage">{{ validateMessage }}</small>
    <small v-if="!!hint && !validateMessage">{{ hint }}</small>
  </div>
</template>

<script>
import { computed, ref, getCurrentInstance, watch } from '@vue/composition-api';
import validator from 'validator';

import { PASSWORD_MIN_LENGTH } from '@/consts';
import { isNumber } from '@/helpers';

export default {
  name: 'TextInput',
  props: {
    placeholder: String,
    label: String,
    id: String,
    roundedEdge: Boolean,
    withShadow: Boolean,
    borderless: Boolean,
    borderbottom: Boolean,
    dynamicLabel: Boolean,
    selectOnFocus: Boolean,
    iconPosition: {
      type: String,
      default: 'left'
    },
    hint: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    required: {
      type: Boolean,
      default: false
    },
    validate: {
      type: Function,
      default: () => ''
    },
    format: {
      type: Function,
      default: (val) => val
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    textarea: {
      type: Boolean,
      default: false,
    },
    bounce: {
      type: Number,
      default: 0
    }
  },
  setup(props, { emit, slots, listeners, refs }) {
    const vm = getCurrentInstance();

    const validateMessage = ref('');
    const isValid = ref(false);
    const cacheValue = ref(props.value);
    const input = ref(null);
    const bounceTimeout = ref(null);

    const wrappedValue = computed({
      get() { return cacheValue.value; },
      set(value) {
        cacheValue.value = value;
      }
    });

    const hasIcon = computed(() => !!slots.icon);
    const inputListeners = {
      ...listeners,
      click() {
        input.value.focus();
        emit('click');
      },
      input(event) {
        if (bounceTimeout.value) {
          clearTimeout(bounceTimeout.value);
        }

        bounceTimeout.value = setTimeout(() => {
          const val = props.format(event.target.value);

          cacheValue.value = val;
          emit('input', val);
        }, props.bounce);
      },
      focus(event) {
        if (props.selectOnFocus) {
          input.value.select();
        }

        emit('focus', event);
      }
    };

    const alert = (msg) => {
      validateMessage.value = msg;
    };

    const clearValidateMessage = () => {
      validateMessage.value = '';
    };

    const validateInput = () => {
      let val = wrappedValue.value;
      if (!!val && !!val.trim) val = val.trim();

      if ((props.required) && !val) {
        alert(vm.$i18n.t('REQUIRED_FIELD'));
        return false;
      }

      if (props.type === 'phoneNumber') {
        const vnPhoneRegex = /(\+84|0)((9|3|7|8|5)+([0-9]{8})\b)/g;
        if (!(vnPhoneRegex.test(val))) {
          alert(vm.$i18n.t('INVALID_PHONENUMBER'));
          return false;
        }
      }

      if (props.type === 'positiveNumber') {
        if (!isNumber(val)) {
          alert(vm.$i18n.t('INVALID_NOT_NUMBER'));
          return false;
        }

        if (!(/^-?\d*(\.[0-9]{1,3})?$/).test(val)) {
          alert(vm.$i18n.t('INVALID_NUMBER'));
          return false;
        }

        if (val < 0) {
          alert(vm.$i18n.t('INVALID_POSITIVE_NUMBER'));
          return false;
        }
      }

      if ((props.type === 'email') && !(val && validator.isEmail(val))) {
        alert(vm.$i18n.t('INVALID_EMAIL'));
        return false;
      }

      if ((props.type === 'password') && (val.length < PASSWORD_MIN_LENGTH)) {
        alert(vm.$i18n.t('PASSWORD_MUST_BE_AT_LEAST', { min: PASSWORD_MIN_LENGTH }));
        return false;
      }

      const msg = props.validate(val);

      if (msg) {
        alert(vm.$i18n.t(msg));
        return false;
      }

      alert('');
      return true;
    };

    const blur = () => {
      refs.input.blur();
    };

    watch(props, (newProps) => {
      cacheValue.value = newProps.format(newProps.value);
    }, {
      immediate: true
    });

    watch(wrappedValue, () => {
      isValid.value = validateInput();
      if(wrappedValue.value === '' && !props.required) {
        alert('');
      }
    });

    return {
      validateMessage,
      alert,
      isValid,
      hasIcon,
      inputListeners,
      wrappedValue,
      blur,
      input,
      validateInput,
      clearValidateMessage
    };
  }
};
</script>

<style lang="scss">
@import '~@/assets/scss/colors';

.text-input {
  display: inline-block;
  position: relative;
  width: 100%;

  .icon {
    color: $independence;
    position: absolute;
    top: 20px;
    left: 15px;
    transform: translateY(-50%);
    color: $lavenderGrey;
  }

  label {
    font-weight: bold;
    padding-left: 2px;
    color: $geranium;
    font-size: 14px;
    margin-bottom: 3px;
    pointer-events:none;

    span {
      color: $geranium;
    }
  }

  input, textarea {
    border: 1px solid $independence;
    border-radius: 0;
    padding: 3px 11px;
    font-display: fallback;
    font-family: "Source Sans Pro", Arial, Tahoma;
    font-size: 15px;
    background-color: $babyPowder;
    color: $independence;
    width: 100%;
    height: 40px;

    &::placeholder {
      color: $alto;
    }

    &:active, &:focus {
      outline: none;
    }
  }

  textarea {
    overflow: hidden;
    height: auto;
    padding: 0;
  }

  small {
    &.red {
      color: $geranium;
    }
  }

  &.has-icon {
    input {
      padding-left: 40px;
    }

    &.icon-right {
      input {
        padding-left: 10px;
        padding-right: 40px;
      }

      .icon {
        left: auto;
        right: 15px;
      }
    }
  }

  &.rounded-edge {
    input {
      border-radius: 23px;
    }
  }

  &.with-shadow {
    input {
      box-shadow: 0px 3px 6px #00000029;
    }
  }

  &.borderless {
    input {
      border: 0
    }
  }

  &.borderbottom {
    transform: translateY(20px);
    padding-bottom: 20px;
    position: relative;

    label{
      color: $blueNcs;
      padding-left: 0px;
      position: absolute;
      top: 0;
      left: 0;
      height: 30px;
      line-height: 30px;
      cursor:text;
      transition: all 200ms ease-out;
      z-index:10;
    }

    input, textarea {
      padding-left: 0px;
      width: 100%;
      height: 30px;
      box-sizing: border-box;
      line-height: 30px;
      font-size:14px;
      border:0;
      background: none;
      outline: none;
      border-bottom:1px solid $alto;
      border-radius: 0;
      -webkit-appearance: none;

      &:focus, &:not(:placeholder-shown) {
        &~label{
          transform: translateY(-20px);
          font-size:0.825em;
          cursor:default;
        }
        &~span {
          color:$blueNcs
        }
      }

      &:focus{
        &~.underline{
          width: 100%;
        }
      }
    }

    textarea {
      height: auto;
      border: 1px solid $alto;
      margin-bottom: -7px;
      margin-top: 7px;
      padding: 3px 7px;
      border-radius: 2px;
    }

    .underline{
      content:'';
      display: block;
      position: absolute;
      left:0;
      width: 0;
      height: 2px;
      background: $blueNcs;
      transition: all 200ms ease-out;
    }

    &.disabled {
      label {
        color: $grey;
      }
    }

    &.has-icon {

      &.icon-right {
        input {
          padding-left: 2px;
          padding-right: 40px;
        }

        .icon {
          left: auto;
          right: 15px;
          top: 13px;
        }
      }
    }
  }

  &.dynamic-label {
    label {
      position: absolute;
      top: 0px;
      left: 0px;
      height: 40px;
      padding: 9px 11px;
      font-size: 15px;
      font-weight: normal;
      transition: all .2s ease-in-out;
      color: $lavenderGrey;
    }

    &.has-icon {
      label {
        padding-left: 40px;
      }
    }

    &.icon-right {
      label {
        padding-left: 11px;
        padding-right: 40px;
      }
    }

    label.mini {
      font-size: 12px;
      font-weight: bold;
      height: auto;
      padding: 3px 7px;
      background-color: $babyPowder;
      left: 15px;
      transform: translateY(-50%);
      z-index: 9;
      color: $independence;
    }

    &.disabled {
      input {
        color: $silver;
      }
      label.mini {
        color: $silver;
      }
      input {
        border: 1px solid $silver;
      }
    }
  }
}
</style>
